import React, {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {signOut} from 'firebase/auth';
import moment from 'moment';

import {auth} from '-/firebase';
import {UserContext} from '-/contexts/User';
import {callFunction} from '-/data/utils';

import AppBanner from './AppBanner';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// An operator session (docs/architecture/operator-identity-plan.md) is the
// platform operator inside a department's data, on a token with a hard end
// time. Say so on every page, with the expiry, and offer the early exit that
// revokes the session server-side rather than just dropping the tab. Not
// dismissible for the same reason the station banner isn't. Red, not the
// station banner's blue: this is a privileged session, not a limited one.
//
// The marker is a claim on the ID token (`operator: true`), not a field on
// currentUser — a legacy password-based superadmin has no session to show.
export default function OperatorBanner() {
    const navigate = useNavigate();
    const {currentUser} = useContext(UserContext);
    const [session, setSession] = useState(null);

    const uid = currentUser?.uid;
    const isSuperAdmin = currentUser?.superadmin === true;

    useEffect(() => {
        if (!isSuperAdmin || !auth.currentUser) {
            setSession(null);
            return undefined;
        }

        let cancelled = false;
        auth.currentUser.getIdTokenResult()
            .then(({claims}) => {
                if (cancelled) return;
                setSession(claims?.operator === true ? {expiresAt: claims.opExp} : null);
            })
            .catch(() => {
                if (!cancelled) setSession(null);
            });

        return () => {
            cancelled = true;
        };
    }, [uid, isSuperAdmin]);

    if (!session) return null;

    const onSignOut = async () => {
        try {
            await callFunction('operatorLogout');
        } catch (e) {
            // The session is ending either way; the api-side revoke is best
            // effort here and opExp is the backstop.
        }
        localStorage.removeItem('impersonating');
        await signOut(auth);
        navigate('/login');
    };

    // Clock time AND the distance: "ends at 9:07 PM" on its own reads like a
    // mistake at five in the afternoon, when it is just the default four hours.
    const expiry = session.expiresAt ? moment(session.expiresAt) : null;

    return (
        <AppBanner
            tone="danger"
            icon={<AdminPanelSettingsIcon />}
            action={{label: 'End Session', onClick: onSignOut, testId: 'operator-banner-signout'}}
            testId="operator-banner"
        >
            <strong>Operator session</strong>
            {expiry && <React.Fragment> &mdash; ends at {expiry.format('h:mm A')} ({expiry.fromNow()})</React.Fragment>}
            . Every action here is attributed to the platform operator.
        </AppBanner>
    );
}
